import { Subscription } from 'rxjs';

import {
  AdministrativeInvoiceDoctorService,
  InvoiceDoctorRow,
  InvoiceDoctorRowStatus,
} from 'src/app/services/administrative-invoice-doctor.service';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';

type RowActionState = 'idle' | 'loading' | 'done' | 'error';

@Component({
  selector: 'app-invoice-doctor',
  templateUrl: './invoice-doctor.component.html',
  styleUrls: ['./invoice-doctor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceDoctorComponent implements OnInit, OnDestroy {
  readonly displayedColumns: string[] = [
    'paidAt',
    'patient',
    'doctor',
    'doctorTransferAmount',
    'indicationStatus',
    'invoiceStatus',
    'action',
  ];
  readonly dataSource = new MatTableDataSource<InvoiceDoctorRow>([]);
  readonly filter: FormGroup;

  availableDoctors: Array<{ id: number; name: string }> = [];
  loading = false;
  error: string | null = null;
  searched = false;

  private readonly rowStateByInvoice = new Map<number, RowActionState>();
  private listSub?: Subscription;
  private emitSub?: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: AdministrativeInvoiceDoctorService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.filter = this.fb.group({
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      doctorIds: [[]],
    });
  }

  ngOnInit(): void {
    // user-triggered search (RN1)
  }

  ngOnDestroy(): void {
    this.listSub?.unsubscribe();
    this.emitSub?.unsubscribe();
  }

  trackByInvoice = (_: number, row: InvoiceDoctorRow): number | string =>
    row.invoiceDoctorId ?? row.indicationId;

  rowActionState(row: InvoiceDoctorRow): RowActionState {
    if (row.invoiceDoctorId == null) return 'idle';
    return this.rowStateByInvoice.get(row.invoiceDoctorId) ?? 'idle';
  }

  hasActionButton(status: InvoiceDoctorRowStatus): boolean {
    return status === 'ELIGIBLE' || status === 'RETRY';
  }

  isBlocked(status: InvoiceDoctorRowStatus): boolean {
    return status === 'BLOCKED';
  }

  isProcessing(status: InvoiceDoctorRowStatus): boolean {
    return status === 'PROCESSING';
  }

  isDone(status: InvoiceDoctorRowStatus): boolean {
    return status === 'DONE';
  }

  onSearch(): void {
    if (this.filter.invalid) {
      this.filter.markAllAsTouched();
      return;
    }
    const { startDate, endDate, doctorIds } = this.filter.value;
    this.loading = true;
    this.error = null;
    this.searched = true;
    this.cdr.markForCheck();

    this.listSub?.unsubscribe();
    this.listSub = this.service
      .list({
        startDate: this.toIsoDate(startDate),
        endDate: this.toIsoDate(endDate),
        doctorIds: doctorIds ?? [],
      })
      .subscribe({
        next: (rows) => {
          this.dataSource.data = rows;
          this.availableDoctors = this.deriveDoctors(rows);
          this.rowStateByInvoice.clear();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.loading = false;
          this.error =
            err?.error?.message ||
            'Não foi possível carregar a lista de NFS-e médicas.';
          this.cdr.markForCheck();
        },
      });
  }

  onEmit(row: InvoiceDoctorRow): void {
    if (row.invoiceDoctorId == null) {
      this.snackBar.open(
        'NFS-e ainda não criada — execução real chegará na PV-1376.',
        'OK',
        { duration: 5000 }
      );
      return;
    }

    this.snackBar.open(
      'A execução real da emissão de NFS-e será habilitada quando a PV-1376 for liberada.',
      'OK',
      { duration: 5000 }
    );

    // Quando PV-1376 sair, basta trocar o bloco acima por:
    //   this.rowStateByInvoice.set(row.invoiceDoctorId, 'loading');
    //   this.emitSub = this.service.emit(row.invoiceDoctorId).subscribe({ ... });
  }

  private deriveDoctors(rows: InvoiceDoctorRow[]): Array<{ id: number; name: string }> {
    const seen = new Map<number, string>();
    for (const r of rows) {
      if (r.doctor.id != null && r.doctor.name) {
        seen.set(r.doctor.id, r.doctor.name);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR')
    );
  }

  private toIsoDate(date: Date | string): string {
    const d = date instanceof Date ? date : new Date(date);
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }
}
